interface KeyValuePair {
	[key: string]: number | string;
}

/**
 * Converts an enum to an an array of objects containing the enum entry name and value.
 * @template T
 * @param forEnum The enum you want to convert.
 * @param namePropertyKeyName The name you want for the name property. Defaults to "name".
 * @param valuePropertyKeyName The name you want for the value property. Defaults to "value".
 * @returns Array of {T} containing enum entry name and value.
 */
export function getNamesAndValuesOfEnum<T extends {}>(
	forEnum: KeyValuePair,
	namePropertyKeyName: string = "name",
	valuePropertyKeyName: string = "value"
): T[] {
	return getNamesOfEnum(forEnum).map(
		name =>
			({
				[namePropertyKeyName]: name,
				[valuePropertyKeyName]: forEnum[name]
			} as T)
	);
}

/**
 * Converts an enum to an array containing the entry names.
 * @param forEnum The enum you want to retrieve the names for.
 * @returns Array of enum entry names.
 */
export function getNamesOfEnum(forEnum: KeyValuePair): string[] {
	return getObjValuesOfEnum(forEnum).filter(value => typeof value === "string") as string[];
}

/**
 * Converts an enum to an array containing the entry values.
 * @param forEnum The enum you want to retrieve the values for.
 * @returns Array of enum entry values.
 */
export function getValuesOfEnum(forEnum: KeyValuePair): number[] {
	return getObjValuesOfEnum(forEnum).filter(value => typeof value === "number") as number[];
}

/**
 * Converts an enum to an array containing the entry names and values.
 * @param forEnum The enum you want to retrieve the names and values for.
 * @returns Array of enum entry names and values.
 */
function getObjValuesOfEnum(forEnum: KeyValuePair): (number | string)[] {
	return Object.keys(forEnum).map(key => forEnum[key]);
}
